import { useState, useEffect, useRef, useCallback } from "react";
import { useMatch, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FileText, UserCircle, Building2, ClipboardList, MapPin, Wallet, Clock, Landmark,
  GraduationCap, History, Rocket, LifeBuoy, X, Link2,
  CalendarDays, ChevronsDownUp, ChevronsUpDown, Eye, EyeOff,
} from "lucide-react";

import { api, errMsg, errStatus } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useVirtualHr } from "../lib/virtualHr.js";
import { useAuth } from "../hooks/useAuth.js";
import { useLookup } from "../context/LookupContext.js";
import { useLeaveTypeConfig } from "../context/LeaveTypeConfigContext.js";
import { uploadDocumentWithAI, uploadDocumentOnly, parseOfferLetterPreview, parseResumePreview, persistExtractedData, SmartBitRejection } from "../lib/resumeUpload.js";
import { DUPLICATE_PHONE_MESSAGE, firstDuplicatePhoneIndex } from "../lib/emergencyContactRules.js";
import { AddressAutocompleteInput } from "../lib/addressAutocomplete.js";
import { EmployeeFormShell } from "../components/employee-form/EmployeeFormShell.js";
import { SectionCollapseProvider, useSectionCollapseState } from "../components/employee-form/SectionCollapseContext.js";
import { SectionPrivacyProvider, useSectionPrivacyState } from "../components/employee-form/SectionPrivacyContext.js";
import { usePageBack } from "../context/PageBackContext.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { ADP_LOCKED_FIELDS, HR_ROLES, ADMIN_ROLES as SR_HR_ROLES, MGR_ROLES, ASSIGNABLE_ROLES, SALARY_ROLES, COMP_VERIFY_ROLES } from "@hrms/types";
import { DocumentLightbox, type LightboxTarget } from "../components/DocumentLightbox.js";
import { type DocVersion } from "../components/DocVersionHistory.js";
import { DocumentsOverviewStrip, type DocSummary } from "../components/DocumentsOverviewStrip.js";
import { SectionAnchorNav, type NavSection, type SectionStatus } from "../components/employee-form/SectionAnchorNav.js";
import { HRReviewPanel, EmployeeMiniProfileBar, type HRSectionStatus } from "../components/employee-form/HRReviewPanel.js";
import { type LeaveBalanceRow } from "../components/LeaveInsightCards.js";
import { inp, disabledInp, PendingBadge, SectionReadOnlyProvider, type FieldAccess } from "../components/employee-form/sectionShared.js";
import { EmergencyContactsSection } from "../components/employee-form/sections/EmergencyContactsSection.js";
import { LeaveSection, type LeaveRequestRow } from "../components/employee-form/sections/LeaveSection.js";
import { AssignedAssetsSection } from "../components/employee-form/sections/AssignedAssetsSection.js";
import { IdentitySection } from "../components/employee-form/sections/IdentitySection.js";
import { BankDetailsSection } from "../components/employee-form/sections/BankDetailsSection.js";
import { PersonalInfoSection } from "../components/employee-form/sections/PersonalInfoSection.js";
import { AddressSection } from "../components/employee-form/sections/AddressSection.js";
import { SkillsHistorySection } from "../components/employee-form/sections/SkillsHistorySection.js";
import { ChangeHistorySection, type AuditEntry } from "../components/employee-form/sections/ChangeHistorySection.js";
import { WorkScheduleSection, type ShiftRecord } from "../components/employee-form/sections/WorkScheduleSection.js";
import { StatutorySection } from "../components/employee-form/sections/StatutorySection.js";
import { CompensationSection, type CompSnapshotFull, type CompHistoryItem } from "../components/employee-form/sections/CompensationSection.js";
import { RoleOrgSection, type ManagerOption } from "../components/employee-form/sections/RoleOrgSection.js";
import { DocumentsSection } from "../components/employee-form/sections/DocumentsSection.js";
import { OnboardingSection, CHECKLIST_DOC_TITLES, REQUIRED_DOC_TYPES, type OnboardingRecord } from "../components/employee-form/sections/OnboardingSection.js";
import { useToast } from "@/context/ToastContext.js";
import { EmployeeProbationBadge } from "../components/EmployeeProbationBadge.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Department { id: string; name: string; code: string; office: { id: string; name: string } | null }
interface Office     { id: string; name: string; city: string; country: string }
interface OrgMeta   { offices: Office[]; departments: Department[] }

type VerifyTarget = LightboxTarget;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["role"] as string ?? "";
  } catch { return ""; }
}

function decodeEmployeeId(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["employeeId"] as string ?? "";
  } catch { return ""; }
}

// Fields already persisted by ensureEmployee()'s create POST — handleSave()'s
// follow-up PATCH must not re-send these on the save that just created the
// employee, since that PATCH is permission-gated for editing an *existing*
// record and would 422 for roles (e.g. Regional HR) that can create employees
// but lack DIRECT edit rights on these same fields afterward.
const FIELDS_SET_BY_CREATE = new Set([
  "firstName", "lastName", "employeeType", "role", "designation",
  "departmentId", "officeId", "managerId", "reportingManagerId", "workLocation",
  "hireDate", "probationEndDate", "noticePeriodDays", "currency", "costCentre",
  "baseSalary", "personalEmail",
]);

const EMPLOYEE_TYPES = ["FULL_TIME", "PART_TIME", "CONTRACTOR", "INTERN"];
const ROLES: string[] = ASSIGNABLE_ROLES;

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmployeeProfilePage() {
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const vhr = useVirtualHr();

  // API feedback goes to the global toaster rather than an inline strip inside a
  // section: the strip could sit off-screen in a collapsed or scrolled-away card,
  // and nothing cleared it. These keep the old setter shape so the existing call
  // sites read the same -- the `setX("")` resets simply become no-ops, which is
  // what you want for a message that dismisses itself.
  const notifyError = useCallback((m: string) => { if (m) toast.show(m, "error"); }, [toast]);
  const notifySuccess = useCallback((m: string) => { if (m) toast.show(m, "success"); }, [toast]);
  const { accessToken } = useAuth();
  const viewerRole = accessToken ? decodeRole(accessToken) : "";
  const viewerEmployeeId = accessToken ? decodeEmployeeId(accessToken) : "";
  const isHR         = HR_ROLES.has(viewerRole);
  const isSrHR       = SR_HR_ROLES.has(viewerRole);
  const isManager    = MGR_ROLES.has(viewerRole);
  // Rostering is a manager responsibility: TEAM_LEAD is in MGR_ROLES for approvals
  // but may not set shifts, matching the shift endpoint's own rule.
  const canEditShift = isHR || viewerRole === "MANAGER" || viewerRole === "SR_MANAGER";
  // View-only: the /view route, or a non-HR user on their own record (even via /edit).
  const isViewRoute = useMatch("/employees/:id/view") !== null;
  const readOnly = !isCreate && (isViewRoute || (!isHR && !!viewerEmployeeId && id === viewerEmployeeId));
  const workLocations = useLookup("WORK_LOCATION");
  const genders = useLookup("GENDER");
  const maritalStatuses = useLookup("MARITAL_STATUS");
  const leaveTypeConfig = useLeaveTypeConfig();
  const bloodGroups = useLookup("BLOOD_GROUP");

  // ── Permission map (edit mode) ─────────────────────────────────────────────
  const [permissions, setPermissions] = useState<Record<string, FieldAccess>>({});
  // Which employee id the form has been hydrated for. Keyed by id rather than a
  // plain boolean so React 18 StrictMode's double-invoke still does not double-
  // fetch, while a route change to a DIFFERENT employee re-hydrates instead of
  // leaving the previous employee's data on screen under the new URL.
  const loadedForId = useRef<string | null>(null);

  // ── Org meta / managers ───────────────────────────────────────────────────
  const [orgMeta, setOrgMeta] = useState<OrgMeta>({ offices: [], departments: [] });
  const [managers, setManagers] = useState<ManagerOption[]>([]);

  // ── Employee (after creation / in edit mode) ──────────────────────────────
  const [employeeId, setEmployeeId] = useState<string | null>(id ?? null);
  const [employeeCode, setEmployeeCode] = useState("");
  // Create mode: set once the hire exists, so the first card can switch from
  // "Save & create hire" to the onboarding-link action without leaving the page.
  const [created, setCreated] = useState<{ employeeId: string; employeeCode: string } | null>(null);
  // What the create POST stored for FIELDS_SET_BY_CREATE, so a later save from
  // the same card can tell an untouched field from an edited one.
  const createdFieldValues = useRef<Record<string, unknown> | null>(null);
  const [status, setStatus] = useState("");
  const [silentCreating, setSilentCreating] = useState(false);

  // ── Section nav (3-panel shell) ───────────────────────────────────────────
  const EDIT_SECTIONS = [
    { id: "documents",    label: "Documents",        icon: <FileText size={14} /> },
    { id: "identity",     label: "Identity",         icon: <UserCircle size={14} /> },
    { id: "role",         label: "Role & Org",       icon: <Building2 size={14} /> },
    { id: "personal",     label: "Personal Info",    icon: <ClipboardList size={14} /> },
    { id: "address",      label: "Address",          icon: <MapPin size={14} /> },
    { id: "compensation", label: "Compensation",     icon: <Wallet size={14} /> },
    { id: "schedule",     label: "Work Schedule",    icon: <Clock size={14} /> },
    { id: "leave",        label: "Leave",            icon: <CalendarDays size={14} /> },
    { id: "bank",         label: "Bank Details",     icon: <Landmark size={14} /> },
    { id: "contacts",     label: "Emergency Contacts", icon: <LifeBuoy size={14} /> },
    { id: "skills",       label: "Skills & History", icon: <GraduationCap size={14} /> },
    { id: "onboarding",   label: "Onboarding",       icon: <Rocket size={14} /> },
    { id: "history",      label: "Change History",   icon: <History size={14} /> },
  ] as const;
  type EditSectionId = typeof EDIT_SECTIONS[number]["id"];

  const [activeSection, setActiveSection] = useState<EditSectionId>(isCreate ? "role" : "documents");
  const sectionRefs: Record<EditSectionId, React.RefObject<HTMLDivElement>> = {
    documents:    useRef<HTMLDivElement>(null),
    identity:     useRef<HTMLDivElement>(null),
    role:         useRef<HTMLDivElement>(null),
    personal:     useRef<HTMLDivElement>(null),
    address:      useRef<HTMLDivElement>(null),
    compensation: useRef<HTMLDivElement>(null),
    schedule:     useRef<HTMLDivElement>(null),
    leave:        useRef<HTMLDivElement>(null),
    bank:         useRef<HTMLDivElement>(null),
    contacts:     useRef<HTMLDivElement>(null),
    skills:       useRef<HTMLDivElement>(null),
    history:      useRef<HTMLDivElement>(null),
    onboarding:   useRef<HTMLDivElement>(null),
  };
  const centerRef = useRef<HTMLDivElement>(null);

  // Leaves the page, so it renders in the top bar (TopHeaderBar draws the
  // SpecularButton), never inside the page body.
  usePageBack("/employees", "Back to Employees");

  // Sections start collapsed on an existing employee and open while creating one.
  const sectionCollapse = useSectionCollapseState(isCreate);
  const sectionPrivacy = useSectionPrivacyState(!isCreate);
  // A nav click has to open the target section before scrollIntoView can find
  // where it ends up, so the scroll waits a render for the body to mount.
  const [pendingScroll, setPendingScroll] = useState<EditSectionId | null>(null);

  // ── Tab (kept for audit fetch trigger) ────────────────────────────────────
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);

  // ── Form state ────────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailSuggested, setEmailSuggested] = useState(false);
  const [employeeType, setEmployeeType] = useState("FULL_TIME");
  const [role, setRole] = useState("EMPLOYEE");
  const [designation, setDesignation] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [managerId,          setManagerId]          = useState("");
  const [reportingManagerId, setReportingManagerId] = useState("");
  const [sameAsHR,           setSameAsHR]           = useState(false);
  const isDirectMgr = isManager && (managerId === viewerEmployeeId || reportingManagerId === viewerEmployeeId);
  const canViewLeaveTab = !isCreate && (isHR || isDirectMgr);

  const [leaveBalance, setLeaveBalance] = useState<LeaveBalanceRow[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequestRow[]>([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const leaveTypes = useLookup("LEAVE_TYPE");
  const [workLocation, setWorkLocation] = useState(() => localStorage.getItem("hrms_last_workLocation") ?? "BANGALORE");
  const [hireDate, setHireDate] = useState("");
  const [probationEndDate, setProbationEndDate] = useState("");

  const [title, setTitle] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [pendingResumeFile, setPendingResumeFile] = useState<File | null>(null);
  const [pendingOfferLetterFile, setPendingOfferLetterFile] = useState<File | null>(null);
  // Kept separately from the pending files, which are cleared once uploaded, so
  // the card still names what HR attached after the save.
  const [attachedNames, setAttachedNames] = useState<{ offerLetter: string | null; resume: string | null }>({ offerLetter: null, resume: null });
  // Fields the offer-letter preview returned, attached to the stored document
  // after the save so it carries its extraction without a second AI call.
  const [offerLetterFields, setOfferLetterFields] = useState<Record<string, unknown> | null>(null);
  const [offerLetterLoading, setOfferLetterLoading] = useState(false);
  const [offerLetterMsg, setOfferLetterMsg] = useState("");
  const [offerLetterCtcNote, setOfferLetterCtcNote] = useState("");
  const [ocrName, setOcrName] = useState<Record<string, string>>({});
  const [ackedConflicts, setAckedConflicts] = useState<Set<string>>(new Set());

  const panInvalid     = panNumber.length > 0     && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNumber);
  const aadhaarInvalid = aadhaarNumber.length > 0 && !/^[2-9][0-9]{11}$/.test(aadhaarNumber);

  // HRMS-0633: offer-letter AI already extracted the annual CTC. Expose it as a
  // plain positive number for CompensationSection; null when nothing is parsed.
  const offerLetterCtc: number | null = (() => {
    const raw = offerLetterFields?.["annualCtc"];
    if (raw == null) return null;
    const n = Number(String(raw).replace(/[^\d.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const isPincodeInvalid = (v: string, country: string) => {
    if (!v) return false;
    if (!country || country === "IN" || country === "INDIA") return !/^[1-9][0-9]{5}$/.test(v);
    if (country === "US" || country === "USA") return !/^\d{5}(-\d{4})?$/.test(v);
    return false;
  };
  const isIfscInvalid    = (v: string) => v.length > 0 && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(v);
  const isEmailInvalid   = (v: string) => v.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const [residentialAddress, setResidentialAddress] = useState("");
  const [residentialAddressLine2, setResidentialAddressLine2] = useState("");
  const [residentialCity, setResidentialCity] = useState("");
  const [residentialState, setResidentialState] = useState("");
  const [residentialDistrict, setResidentialDistrict] = useState("");
  const [residentialPincode, setResidentialPincode] = useState("");
  const [residentialLat, setResidentialLat] = useState<number | null>(null);
  const [residentialLng, setResidentialLng] = useState<number | null>(null);
  const [permanentAddress, setPermanentAddress] = useState("");
  const [permanentAddressLine2, setPermanentAddressLine2] = useState("");
  const [permanentCity, setPermanentCity] = useState("");
  const [permanentState, setPermanentState] = useState("");
  const [permanentDistrict, setPermanentDistrict] = useState("");
  const [permanentPincode, setPermanentPincode] = useState("");
  const [permanentLat, setPermanentLat] = useState<number | null>(null);
  const [permanentLng, setPermanentLng] = useState<number | null>(null);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  const [ec1Name,     setEc1Name]     = useState("");
  const [ec1Phone,    setEc1Phone]    = useState("");
  const [ec1Relation, setEc1Relation] = useState("");
  const [ec2Name,     setEc2Name]     = useState("");
  const [ec2Phone,    setEc2Phone]    = useState("");
  const [ec2Relation, setEc2Relation] = useState("");
  // Two emergency contacts on one number leave HR with nobody else to call if
  // that number is unreachable, so the save is refused until it is changed.
  const ecDuplicatePhone = firstDuplicatePhoneIndex([ec1Phone, ec2Phone]) !== -1;

  const [baseSalary, setBaseSalary] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [noticePeriodDays, setNoticePeriodDays] = useState("90");
  const [costCentre, setCostCentre] = useState("");

  const [esiNumber, setEsiNumber] = useState("");
  const [uanNumber, setUanNumber] = useState("");
  const [medicalCard, setMedicalCard] = useState("");
  const [pensionStatus, setPensionStatus] = useState("");
  const [appraisalLastDate, setAppraisalLastDate] = useState("");
  const [appraisalDueDate, setAppraisalDueDate] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [statutoryEditing, setStatutoryEditing] = useState(false);
  const [savingStatutory, setSavingStatutory] = useState(false);
  const [statutorySaved, setStatutorySaved] = useState(false);

  // ── Assigned assets (read-only, edit mode only) ───────────────────────────
  const [empAssets, setEmpAssets] = useState<{
    id: string; assignedAt: string;
    asset: { assetTag: string; name: string; category: string; condition: string };
  }[]>([]);

  // ── Onboarding section (HR/Senior-HR only) ────────────────────────────────
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [onboardingRecord, setOnboardingRecord] = useState<OnboardingRecord | null>(null);
  // Stays inline beside the handbook button: it is a link to read and copy, not
  // a status message, so it must persist rather than time out like a toast.
  const [signLink, setSignLink] = useState<
    { url: string; expiresAt: string; emailed: boolean; emailError: string | null; sentTo: string } | null
  >(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [initiatingOnboarding, setInitiatingOnboarding] = useState(false);
  const [recheckingDocId, setRecheckingDocId] = useState<string | null>(null);
  const [togglingItemId, setTogglingItemId] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const [adStatus, setAdStatus] = useState<{ adAccountStatus: string | null; adLastError: string | null } | null>(null);
  const [adLoading, setAdLoading] = useState(false);
  const [adCreating, setAdCreating] = useState(false);
  const [adConfirmOpen, setAdConfirmOpen] = useState(false);
  const [adExistsPrompt, setAdExistsPrompt] = useState<{ upn: string } | null>(null);
  const [adResult, setAdResult] = useState<{ tempPassword: string; tempPasswordSentTo: string | null } | null>(null);
  const [hrAcking, setHrAcking] = useState(false);
  const [signedHandbook, setSignedHandbook] = useState<{ handbook: { id: string; title: string; version: string } | null; readUrl: string | null } | null>(null);
  const [payrollChecking, setPayrollChecking] = useState(false);
  const [payrollResult, setPayrollResult] = useState<{ ready: boolean; system: string; missing: string[]; message: string } | null>(null);
  const [activating, setActivating] = useState(false);

  // ── Comp snapshot ─────────────────────────────────────────────────────────
  const [compSnapshot, setCompSnapshot] = useState<CompSnapshotFull | null>(null);
  const [compHistory, setCompHistory]   = useState<CompHistoryItem[]>([]);
  const [showRevDrawer, setShowRevDrawer]     = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false); // revision-history modal, closed on load

  // ── Shift schedule ────────────────────────────────────────────────────────
  const [currentShift,  setCurrentShift]  = useState<ShiftRecord | null>(null);
  const [shiftHistory,  setShiftHistory]  = useState<ShiftRecord[]>([]);
  const [shiftStartHour, setShiftStartHour] = useState(9);
  const [shiftType,     setShiftType]     = useState<"MORNING"|"EVENING"|"NIGHT"|"CUSTOM">("MORNING");
  const [shiftEffFrom,  setShiftEffFrom]  = useState(new Date().toISOString().slice(0,10));
  const [shiftReason,   setShiftReason]   = useState("");
  const [shiftSaving,   setShiftSaving]   = useState(false);
  const [shiftError,    setShiftError]    = useState<string | null>(null);
  const [shiftHistExpanded, setShiftHistExpanded] = useState(false);
  const shiftDragRef = useRef<HTMLDivElement>(null);

  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankIfscCode, setBankIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountHolderName, setBankAccountHolderName] = useState("");
  const [sourceSystem, setSourceSystem] = useState<string | null>(null);

  const [skills, setSkills] = useState("");
  const [education, setEducation] = useState([
    { degree: "", institution: "", fieldOfStudy: "", startYear: "", endYear: "" },
  ]);
  const [workHistory, setWorkHistory] = useState([
    { company: "", title: "", startDate: "", endDate: "", isCurrent: false },
  ]);

  // ── Resume upload ─────────────────────────────────────────────────────────
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeMsg, setResumeMsg] = useState("");

  // ── Save state ────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);

  // ── Document version history ──────────────────────────────────────────────
  const [verifyTarget, setVerifyTarget] = useState<VerifyTarget | null>(null);
  const [docRefreshKeys, setDocRefreshKeys] = useState<Record<string, number>>({});
  const [uploadingDoc, setUploadingDoc] = useState<Record<string, boolean>>({});
  const [hasAnyDoc, setHasAnyDoc] = useState(false);

  function bumpDocRefresh(type: string) {
    setDocRefreshKeys((prev) => ({ ...prev, [type]: (prev[type] ?? 0) + 1 }));
    setHasAnyDoc(true);
    void loadDocs();
  }

  function loadDocs() {
    if (!employeeId) return Promise.resolve();
    return api.get<DocSummary[]>(`/employees/${employeeId}/documents`)
      .then(r => { setHasAnyDoc(r.data.length > 0); setDocs(r.data); })
      .catch((err) => logError("EmployeeProfilePage.loadDocuments", err, { employeeId }));
  }

  async function openDocFromStrip(doc: DocSummary) {
    try {
      const { data } = await api.get<{ mimeType: string | null; versions: DocVersion[] }>(`/documents/${doc.id}/versions`);
      const latest = data.versions[0];
      if (!latest) return;
      setVerifyTarget({
        versionId: latest.id, docType: doc.type, version: latest.version,
        mimeType: data.mimeType, extractedData: latest.extractedData,
        currentStatus: latest.hrVerifiedAt ? "VERIFIED" : latest.hrRejectedAt ? "REJECTED" : "UNVERIFIED",
        isHR: isHR && !readOnly,
        readOnly,
      });
    } catch (err) { logError("EmployeeProfilePage.openDocFromStrip", err, { docId: doc.id }); }
  }

  // ── Persist last-used work location for create mode ─────────────────────────
  useEffect(() => {
    if (isCreate) {
      try { localStorage.setItem("hrms_last_workLocation", workLocation); } catch { /* private mode */ }
    }
  }, [workLocation, isCreate]);

  // ── Probation end date: auto-suggest hireDate + 90 days in create mode ──────
  // Stops once a real probation date is known — typed by HR, or read off the
  // offer letter. Without this the offer letter's own hire date would re-trigger
  // the +90d suggestion and overwrite the probation date from the same letter.
  const probationTouched = useRef(false);
  useEffect(() => {
    if (!isCreate || !hireDate || probationTouched.current) return;
    const d = new Date(hireDate);
    d.setDate(d.getDate() + 90);
    setProbationEndDate(d.toISOString().slice(0, 10));
  }, [hireDate, isCreate]);
  const setProbationEndDateTouched = useCallback((v: string) => {
    probationTouched.current = true;
    setProbationEndDate(v);
  }, []);

  // ── Email suggestion debounce ─────────────────────────────────────────────
  const emailDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (emailDebounce.current) clearTimeout(emailDebounce.current);
    if (!firstName.trim() || !lastName.trim() || email) return;
    emailDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ suggestedEmail: string }>(
          `/employees/suggest-email?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`,
        );
        setEmail(data.suggestedEmail);
        setEmailSuggested(true);
      } catch (err) { logError("EmployeeProfilePage.emailSuggestion", err); }
    }, 600);
    return () => { if (emailDebounce.current) clearTimeout(emailDebounce.current); };
  }, [firstName, lastName, email]);

  // ── Sync residential address when "same as permanent" is checked ─────────
  useEffect(() => {
    if (!sameAsPermanent) return;
    setResidentialAddress(permanentAddress);
    setResidentialAddressLine2(permanentAddressLine2);
    setResidentialCity(permanentCity);
    setResidentialState(permanentState);
    setResidentialDistrict(permanentDistrict);
    setResidentialPincode(permanentPincode);
    setResidentialLat(permanentLat);
    setResidentialLng(permanentLng);
  }, [sameAsPermanent, permanentAddress, permanentAddressLine2, permanentCity, permanentState, permanentDistrict, permanentPincode, permanentLat, permanentLng]);

  // ── Load org meta + managers ──────────────────────────────────────────────
  useEffect(() => {
    api.get<OrgMeta>("/employees/org-meta").then(({ data }) => setOrgMeta(data)).catch((err) => logError("EmployeeProfilePage.loadOrgMeta", err));
    api.get<{ data: ManagerOption[] }>("/employees?limit=500").then(({ data }) => setManagers(data.data)).catch((err) => logError("EmployeeProfilePage.loadManagers", err));
  }, []);

  // ── Load employee data + permissions in edit mode ─────────────────────────
  const loadEmployee = useCallback(async (opts?: { skipOnboarding?: boolean }) => {
    if (!id) return;
    try {
      const [empRes, permRes] = await Promise.all([
        api.get<Record<string, unknown>>(`/employees/${id}`),
        api.get<Record<string, FieldAccess>>(`/employees/${id}/field-permissions`),
      ]);

      setPermissions(permRes.data);
      const e = empRes.data;
      setEmployeeCode(String(e["employeeCode"] ?? ""));
      setStatus(String(e["status"] ?? ""));
      setFirstName(String(e["firstName"] ?? ""));
      setMiddleName(String(e["middleName"] ?? ""));
      setLastName(String(e["lastName"] ?? ""));
      setEmail(String(e["email"] ?? ""));
      setEmployeeType(String(e["employeeType"] ?? "FULL_TIME"));
      setRole(String(e["role"] ?? "EMPLOYEE"));
      setDesignation(String(e["designation"] ?? ""));
      setDepartmentId(String(e["departmentId"] ?? ""));
      setOfficeId(String(e["officeId"] ?? ""));
      const mId  = String(e["managerId"]          ?? "");
      const rmId = String(e["reportingManagerId"] ?? "");
      setManagerId(mId);
      setReportingManagerId(rmId);
      setSameAsHR(rmId !== "" && rmId === mId);
      setWorkLocation(String(e["workLocation"] ?? ""));
      setHireDate(e["hireDate"] ? String(e["hireDate"]).slice(0, 10) : "");
      setProbationEndDate(e["probationEndDate"] ? String(e["probationEndDate"]).slice(0, 10) : "");
      setTitle(String(e["title"] ?? ""));
      setDateOfBirth(e["dateOfBirth"] ? String(e["dateOfBirth"]).slice(0, 10) : "");
      setGender(String(e["gender"] ?? ""));
      setMaritalStatus(String(e["maritalStatus"] ?? ""));
      setBloodGroup(String(e["bloodGroup"] ?? ""));
      setPersonalEmail(String(e["personalEmail"] ?? ""));
      setPersonalPhone(String(e["personalPhone"] ?? ""));
      setLinkedIn(String(e["linkedIn"] ?? ""));
      setPanNumber(String(e["panNumber"] ?? ""));
      setAadhaarNumber(String(e["aadhaarNumber"] ?? ""));
      setResidentialAddress(String(e["residentialAddress"] ?? ""));
      setResidentialAddressLine2(String(e["residentialAddressLine2"] ?? ""));
      setResidentialCity(String(e["residentialCity"] ?? ""));
      setResidentialState(String(e["residentialState"] ?? ""));
      setResidentialDistrict(String(e["residentialDistrict"] ?? ""));
      setResidentialPincode(String(e["residentialPincode"] ?? ""));
      setResidentialLat(e["residentialLat"] != null ? Number(e["residentialLat"]) : null);
      setResidentialLng(e["residentialLng"] != null ? Number(e["residentialLng"]) : null);
      setPermanentAddress(String(e["permanentAddress"] ?? ""));
      setPermanentAddressLine2(String(e["permanentAddressLine2"] ?? ""));
      setPermanentCity(String(e["permanentCity"] ?? ""));
      setPermanentState(String(e["permanentState"] ?? ""));
      setPermanentDistrict(String(e["permanentDistrict"] ?? ""));
      setPermanentPincode(String(e["permanentPincode"] ?? ""));
      setPermanentLat(e["permanentLat"] != null ? Number(e["permanentLat"]) : null);
      setPermanentLng(e["permanentLng"] != null ? Number(e["permanentLng"]) : null);
      setBaseSalary(e["baseSalary"] != null ? String(e["baseSalary"]) : "");
      setCurrency(String(e["currency"] ?? "INR"));
      setNoticePeriodDays(e["noticePeriodDays"] != null ? String(e["noticePeriodDays"]) : "90");
      setCostCentre(String(e["costCentre"] ?? ""));
      setEsiNumber(String(e["esiNumber"] ?? ""));
      setUanNumber(String(e["uanNumber"] ?? ""));
      setMedicalCard(String(e["medicalCard"] ?? ""));
      setPensionStatus(String(e["pensionStatus"] ?? ""));
      setAppraisalLastDate(e["appraisalLastDate"] ? String(e["appraisalLastDate"]).slice(0, 10) : "");
      setAppraisalDueDate(e["appraisalDueDate"] ? String(e["appraisalDueDate"]).slice(0, 10) : "");
      setPoNumber(String(e["poNumber"] ?? ""));
      setBankAccountNumber(String(e["bankAccountNumber"] ?? ""));
      setBankIfscCode(String(e["bankIfscCode"] ?? ""));
      setBankName(String(e["bankName"] ?? ""));
      setBankAccountHolderName(String(e["bankAccountHolderName"] ?? ""));
      setSourceSystem(e["sourceSystem"] ? String(e["sourceSystem"]) : null);
      setEc1Name(String(e["emergencyContact1Name"] ?? ""));
      setEc1Phone(String(e["emergencyContact1Phone"] ?? ""));
      setEc1Relation(String(e["emergencyContact1Relation"] ?? ""));
      setEc2Name(String(e["emergencyContact2Name"] ?? ""));
      setEc2Phone(String(e["emergencyContact2Phone"] ?? ""));
      setEc2Relation(String(e["emergencyContact2Relation"] ?? ""));
      void loadDocs();
      // Fetch audit log eagerly so Change History section is populated on load
      if (!isCreate) {
        api.get<{ data: AuditEntry[] }>(`/audit?resourceType=Employee&resourceId=${id}&limit=100`)
          .then(r => setAuditLog(r.data.data))
          .catch((err) => { if (errStatus(err) !== 403) logError("EmployeeProfilePage.loadAudit", err); });
      }
      // Fetch comp snapshots
      if (!isCreate) {
        api.get<CompSnapshotFull | null>(`/employees/${id}/compensation/current`)
          .then(r => setCompSnapshot(r.data))
          .catch((err) => { if (errStatus(err) !== 403) logError("EmployeeProfilePage.loadCompCurrent", err, { employeeId: id }); });
        api.get<CompHistoryItem[]>(`/employees/${id}/compensation/snapshots`)
          .then(r => setCompHistory(r.data))
          .catch((err) => { if (errStatus(err) !== 403) logError("EmployeeProfilePage.loadCompHistory", err, { employeeId: id }); });
        // Fetch shift schedule
        api.get<ShiftRecord | null>(`/employees/${id}/shift/current`)
          .then(r => {
            setCurrentShift(r.data);
            if (r.data) {
              // Half-hour starts (08:30) are selectable, so keep the minutes.
              const [hh, mm] = r.data.shiftStartHHMM.split(":");
              setShiftStartHour(parseInt(hh ?? "9", 10) + (parseInt(mm ?? "0", 10) >= 30 ? 0.5 : 0));
              setShiftType(r.data.shiftType as "MORNING"|"EVENING"|"NIGHT"|"CUSTOM");
            }
          }).catch((err) => logError("EmployeeProfilePage.loadShiftCurrent", err, { employeeId: id }));
        api.get<ShiftRecord[]>(`/employees/${id}/shift/history`)
          .then(r => setShiftHistory(r.data))
          .catch((err) => logError("EmployeeProfilePage.loadShiftHistory", err, { employeeId: id }));
        api.get<typeof empAssets>(`/employees/${id}/assets`)
          .then(r => setEmpAssets(r.data))
          .catch(() => {});
        // Employees with no onboarding record 404 here. The .catch() swallows it
        // for the UI, but api.ts's response interceptor still ships every
        // non-401 to /logs/client-error -- so a post-save reload skips the probe
        // rather than logging a 404 on every single save.
        if (isHR && !opts?.skipOnboarding) {
          api.get<typeof onboardingRecord>(`/employees/${id}/onboarding`)
            .then(r => setOnboardingRecord(r.data))
            .catch(() => {});
        }
        if (isSrHR) {
          setAdLoading(true);
          api.get<typeof adStatus>(`/employees/${id}/ad-account`)
            .then(r => setAdStatus(r.data))
            .catch((err) => logError("EmployeeProfilePage.loadAdStatus", err, { employeeId: id }))
            .finally(() => setAdLoading(false));
        }
        api.get<{ skills: string; education: Array<Record<string, string>>; workHistory: Array<Record<string, unknown>> }>(`/employees/${id}/skills-history`)
          .then(r => {
            if (r.data.skills) setSkills(r.data.skills);
            if (r.data.education?.length > 0) setEducation(r.data.education.map((e) => ({
              degree: e["degree"] ?? "",
              institution: e["institution"] ?? "",
              fieldOfStudy: e["fieldOfStudy"] ?? "",
              startYear: e["startYear"] ?? "",
              endYear: e["endYear"] ?? "",
            })));
            if (r.data.workHistory?.length > 0) setWorkHistory(r.data.workHistory.map((w) => ({
              company: String(w["company"] ?? ""),
              title: String(w["title"] ?? ""),
              startDate: String(w["startDate"] ?? ""),
              endDate: String(w["endDate"] ?? ""),
              isCurrent: Boolean(w["isCurrent"]),
            })));
          })
          .catch((err) => logError("EmployeeProfilePage.loadWizardState", err));
      }
    } catch (err) {
      if (errStatus(err) === 404) {
        toast.show("This employee could not be found — they may have been removed.", "error");
        navigate("/employees");
      } else {
        logError("EmployeeProfilePage.loadEmployee", err, { employeeId: id });
      }
    }
    // Deps are [id] on purpose. The body closes over ~10 unmemoised values
    // (isCreate, isHR, loadDocs, navigate, toast ...); listing them would rebuild
    // this callback every render and the effect below would refetch in a loop.
    // Same reasoning and shape as EmailTemplatesAdminPage.loadAll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id || loadedForId.current === id) return;
    loadedForId.current = id;
    void loadEmployee();
  }, [id, loadEmployee]);

  // ── Silent create on resume drop (create mode) ────────────────────────────
  const ensureEmployee = useCallback(async (): Promise<string | null> => {
    if (employeeId) return employeeId;
    if (!firstName.trim() || !email.trim() || !employeeCode.trim()) return null;
    if (silentCreating) return null;
    setSilentCreating(true);
    notifyError("");
    try {
      const { data } = await api.post<{ id: string; employeeCode: string; _existed?: boolean }>("/employees", {
        employeeCode: employeeCode.trim(), firstName, lastName, email,
        employeeType, role,
        ...(departmentId ? { departmentId } : {}),
        ...(officeId     ? { officeId }     : {}),
        ...(designation  ? { designation }  : {}),
        ...(workLocation ? { workLocation } : {}),
        ...(hireDate     ? { hireDate }     : {}),
        ...(managerId ? { managerId } : {}),
        ...((sameAsHR ? managerId : reportingManagerId) ? { reportingManagerId: sameAsHR ? managerId : reportingManagerId } : {}),
        ...(probationEndDate ? { probationEndDate } : {}),
        ...(noticePeriodDays ? { noticePeriodDays: parseInt(noticePeriodDays, 10) } : {}),
        ...(currency ? { currency } : {}),
        ...(costCentre ? { costCentre } : {}),
        ...(baseSalary ? { baseSalary: parseFloat(baseSalary) } : {}),
        ...(personalEmail ? { personalEmail } : {}),
      });
      setEmployeeId(data.id);
      setEmployeeCode(data.employeeCode);
      if (data._existed) {
        // Stub already exists — switch to edit mode so fields save to the right record
        navigate(`/employees/${data.id}/edit`);
        return null;
      }
      return data.id;
    } catch (err: unknown) {
      const msg = errMsg(err);
      notifyError(msg ?? "Could not create employee record for resume upload.");
      return null;
    } finally {
      setSilentCreating(false);
    }
  }, [
    employeeId, employeeCode, firstName, lastName, email, employeeType, role, designation, departmentId, officeId,
    workLocation, hireDate, managerId, reportingManagerId, sameAsHR, probationEndDate, noticePeriodDays,
    currency, costCentre, baseSalary, personalEmail, silentCreating,
  ]);

  useEffect(() => {
    if (isCreate || !id || !canViewLeaveTab) return;
    let cancelled = false;
    setLeaveLoading(true);
    Promise.all([
      api.get<LeaveBalanceRow[]>(`/leave/balance/${id}`),
      api.get<{ data: LeaveRequestRow[] }>(`/leave/requests?employeeId=${id}&limit=10`),
    ]).then(([balRes, reqRes]) => {
      if (cancelled) return;
      setLeaveBalance(balRes.data);
      setLeaveRequests(reqRes.data.data);
    }).catch((err) => logError("EmployeeProfilePage.loadLeave", err))
      .finally(() => { if (!cancelled) setLeaveLoading(false); });
    return () => { cancelled = true; };
  }, [id, isCreate, canViewLeaveTab]);

  // Fills the profile from a parsed résumé. The name and date of birth are never taken from
  // a résumé: they come from the Aadhaar / PAN.
  function applyResumeFields(fields: Record<string, unknown>) {
    if (fields["email"])         setPersonalEmail(String(fields["email"]));
    if (fields["personalEmail"]) setPersonalEmail(String(fields["personalEmail"]));
    if (fields["phone"])                     setPersonalPhone(String(fields["phone"]));
    if (fields["linkedIn"])                  setLinkedIn(String(fields["linkedIn"]));
    if (fields["skills"])                    setSkills(String(fields["skills"]));
    if (Array.isArray(fields["education"]) && fields["education"].length > 0) {
      setEducation((fields["education"] as Array<Record<string, unknown>>).map((e) => ({
        degree: String(e["degree"] ?? ""),
        institution: String(e["institution"] ?? ""),
        fieldOfStudy: String(e["fieldOfStudy"] ?? ""),
        startYear: e["startYear"] != null ? String(e["startYear"]) : "",
        endYear: e["endYear"] != null ? String(e["endYear"]) : "",
      })));
    }
    if (Array.isArray(fields["workHistory"]) && fields["workHistory"].length > 0) {
      setWorkHistory((fields["workHistory"] as Array<Record<string, unknown>>).map((w) => ({
        company: String(w["company"] ?? ""),
        title: String(w["title"] ?? ""),
        startDate: w["startDate"] != null ? String(w["startDate"]) : "",
        endDate: w["isCurrent"] ? "" : (w["endDate"] != null ? String(w["endDate"]) : ""),
        isCurrent: Boolean(w["isCurrent"]),
      })));
    }
    if (fields["address"]) {
      const addr = fields["address"] as Record<string, unknown>;
      if (addr["line1"] && !residentialAddress)      setResidentialAddress(String(addr["line1"]));
      if (addr["line2"] && !residentialAddressLine2) setResidentialAddressLine2(String(addr["line2"]));
      if (addr["city"]  && !residentialCity)         setResidentialCity(String(addr["city"]));
      if (addr["state"] && !residentialState)        setResidentialState(String(addr["state"]));
      if (addr["pincode"] && !residentialPincode)    setResidentialPincode(String(addr["pincode"]));
    }
  }

  async function handleResumeFile(file: File) {
    setResumeLoading(true);
    setResumeMsg("");
    // Sirra off for résumés (the Employee box, the master switch, or this document's own box in the admin
    // settings): the file is only attached. Nothing is checked, read or filled in.
    if (!vhr.isDocEnabled("OTHER")) {
      try {
        if (employeeId) await uploadDocumentOnly(employeeId, file, "OTHER", { skipVerify: true });
        else setPendingResumeFile(file); // stored after the employee is created
        setAttachedNames(prev => ({ ...prev, resume: file.name }));
        setResumeMsg("✓ Resume attached.");
        toast.show("Resume attached.", "success");
      } catch (err) {
        logError("EmployeeProfilePage.handleResumeFile.plain", err);
        toast.show(errMsg(err) ?? "Could not attach the resume", "error");
      }
      setResumeLoading(false);
      return;
    }
    try {
      const fields = employeeId
        ? (await uploadDocumentWithAI(employeeId, file, "OTHER", "RESUME")) as Record<string, unknown>
        : await parseResumePreview(file);
      if (!employeeId) setPendingResumeFile(file);
      setAttachedNames(prev => ({ ...prev, resume: file.name }));

      if (fields["name"]) {
        const raw = String(fields["name"]).trim();
        setOcrName(prev => ({ ...prev, résumé: raw }));
        if (!firstName.trim()) {
          const parts = raw.split(/\s+/).filter(Boolean);
          if (parts[0]) setFirstName(parts[0]);
          if (parts.length > 1) setLastName(parts[parts.length - 1]!);
        }
      }
      applyResumeFields(fields);
      setResumeMsg("✓ Resume parsed — review extracted information below before saving.");
      toast.show("Resume parsed — review the extracted information before saving.", "success");
    } catch (err) {
      logError("EmployeeProfilePage.handleResumeFile", err);
      // A refused file says why (e.g. "This looks like an invoice, not a résumé"); other failures keep the old text.
      if (err instanceof SmartBitRejection) {
        setResumeMsg(err.message);
        toast.show(err.message, "warning");
      } else {
        setResumeMsg("Parsing failed or unavailable — fill fields manually.");
        toast.show("Resume parsing failed or unavailable — fill the fields manually.", "warning");
      }
    }
    setResumeLoading(false);
  }

  // Offer letter — create mode. Parsed from the picked file (no employee record
  // exists yet) and held until the save, which stores it and attaches these same
  // fields to the version. Fills only what is still blank, except the hire date,
  // which the letter is the authority on.
  async function handleOfferLetterFile(file: File) {
    setOfferLetterLoading(true);
    setOfferLetterMsg("");
    setPendingOfferLetterFile(file);
    setAttachedNames(prev => ({ ...prev, offerLetter: file.name }));
    try {
      const fields = await parseOfferLetterPreview(file);
      setOfferLetterFields(fields);

      if (fields["candidateName"]) {
        const raw = String(fields["candidateName"]).trim();
        setOcrName(prev => ({ ...prev, "offer letter": raw }));
        if (!firstName.trim()) {
          const parts = raw.split(/\s+/).filter(Boolean);
          if (parts[0]) setFirstName(parts[0]);
          if (parts.length > 1) setLastName(parts[parts.length - 1]!);
        }
      }
      if (fields["personalEmail"] && !personalEmail) setPersonalEmail(String(fields["personalEmail"]));
      if (fields["designation"] && !designation) setDesignation(String(fields["designation"]));
      if (fields["employmentType"] && EMPLOYEE_TYPES.includes(String(fields["employmentType"]))) {
        setEmployeeType(String(fields["employmentType"]));
      }
      if (fields["noticePeriodDays"] != null && Number.isFinite(Number(fields["noticePeriodDays"]))) {
        setNoticePeriodDays(String(Number(fields["noticePeriodDays"])));
      }
      if (fields["currency"]) setCurrency(String(fields["currency"]).toUpperCase());
      if (fields["workLocation"] ?? fields["officeLocation"]) {
        const raw = String(fields["workLocation"] ?? fields["officeLocation"]).trim().toLowerCase();
        const match = workLocations.find(o =>
          o.value.toLowerCase() === raw || o.displayName.toLowerCase() === raw);
        if (match) setWorkLocation(match.value);
      }
      // Office before department: changing the office clears the department.
      let matchedOfficeId = officeId;
      if (fields["officeLocation"] && !officeId) {
        const raw = String(fields["officeLocation"]).trim().toLowerCase();
        const office = orgMeta.offices.find(o =>
          (o.country ?? "").toUpperCase() !== "US" &&
          (o.city.toLowerCase() === raw || o.name.toLowerCase() === raw));
        if (office) { matchedOfficeId = office.id; setOfficeId(office.id); }
      }
      if (fields["department"] && !departmentId && matchedOfficeId) {
        const raw = String(fields["department"]).trim().toLowerCase();
        const dept = orgMeta.departments.find(d =>
          d.office?.id === matchedOfficeId &&
          (d.name.toLowerCase() === raw || d.code.toLowerCase() === raw));
        if (dept) setDepartmentId(dept.id);
      }
      // Dates last, so the probation guard is set before hireDate's effect runs.
      if (fields["probationEndDate"]) {
        probationTouched.current = true;
        setProbationEndDate(String(fields["probationEndDate"]).slice(0, 10));
      }
      if (fields["joiningDate"]) {
        const joining = String(fields["joiningDate"]).slice(0, 10);
        setHireDate(joining);
        if (!fields["probationEndDate"] && fields["probationMonths"] != null) {
          const months = Number(fields["probationMonths"]);
          if (Number.isFinite(months) && months > 0) {
            const d = new Date(joining);
            d.setMonth(d.getMonth() + months);
            probationTouched.current = true;
            setProbationEndDate(d.toISOString().slice(0, 10));
          }
        }
      }
      // CTC is surfaced, never written: an offer letter's headline figure is the
      // annual cost to company, which is not the base salary field.
      if (fields["annualCtc"] != null && String(fields["annualCtc"]).trim() !== "") {
        const amount = Number(String(fields["annualCtc"]).replace(/[^\d.]/g, ""));
        if (Number.isFinite(amount) && amount > 0) {
          const code = String(fields["currency"] ?? currency ?? "INR").toUpperCase();
          const shown = code === "INR" ? amount.toLocaleString("en-IN") : amount.toLocaleString("en-US");
          setOfferLetterCtcNote(`Offer letter states ${code} ${shown} annual CTC — enter it in Compensation.`);
        }
      }

      setOfferLetterMsg("✓ Offer letter parsed — check the fields below.");
      toast.show("Offer letter parsed — check the extracted fields before saving.", "success");
    } catch (err) {
      logError("EmployeeProfilePage.handleOfferLetterFile", err);
      setOfferLetterMsg("Could not read this offer letter — fill the fields manually. It will still be attached on save.");
      toast.show("Offer letter parsing failed — the file is still attached on save.", "warning");
    }
    setOfferLetterLoading(false);
  }

  // ── Helpers for permission-aware field rendering ───────────────────────────
  function canView(field: string): boolean {
    if (isCreate) return true;
    const p = permissions[field];
    return !p || p.canViewInProfile !== "HIDDEN";
  }

  function inputProps(field: string, value: string, onChange: (v: string) => void) {
    if (sourceSystem === "ADP" && ADP_LOCKED_FIELDS.has(field)) {
      return { value, onChange: () => {}, disabled: true as const, style: disabledInp };
    }
    if (isCreate) return { value, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange(e.target.value), style: inp };
    const p = permissions[field];
    const masked = p?.canViewInProfile === "MASKED";
    const editable = !p || (p.canViewInProfile === "FULL" && (p.canEdit === "DIRECT" || p.canEdit === "WITH_APPROVAL") && !p.isLocked);
    return {
      value: masked ? "••••••••" : value,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (editable && !masked) onChange(e.target.value);
      },
      disabled: !editable || masked,
      style: editable && !masked ? inp : disabledInp,
    };
  }

  function datePickerProps(field: string, value: string, changeHandler: (v: string) => void) {
    if (sourceSystem === "ADP" && ADP_LOCKED_FIELDS.has(field)) {
      return { value, onChange: (_: string) => {}, disabled: true as const, style: disabledInp };
    }
    if (isCreate) return { value, onChange: changeHandler, style: inp };
    const p = permissions[field];
    const masked = p?.canViewInProfile === "MASKED";
    const editable = !p || (p.canViewInProfile === "FULL" && (p.canEdit === "DIRECT" || p.canEdit === "WITH_APPROVAL") && !p.isLocked);
    return {
      value: masked ? "" : value,
      onChange: (v: string) => { if (editable && !masked) changeHandler(v); },
      disabled: !editable || masked,
      style: editable && !masked ? inp : disabledInp,
    };
  }

  function pendingBadge(field: string) {
    if (isCreate) return null;
    const p = permissions[field];
    if (!p?.hasPendingChange) return null;
    return <PendingBadge value={p.pendingValue} date={p.pendingAt} />;
  }

  function approvalNote(field: string) {
    if (isCreate) return null;
    const p = permissions[field];
    if (p?.canEdit !== "WITH_APPROVAL" || p.isLocked) return null;
    return (
      <span style={{ fontSize: "10px", color: "var(--color-accent-accent)", fontWeight: 600, marginLeft: "6px" }}>
        Requires approval
      </span>
    );
  }



  // ── IntersectionObserver for section nav ──────────────────────────────────
  useEffect(() => {
    const center = centerRef.current;
    if (!center) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const sid = entry.target.getAttribute("data-section") as EditSectionId | null;
            if (sid) setActiveSection(sid);
          }
        }
      },
      { root: center, threshold: 0.2, rootMargin: "-10% 0px -60% 0px" },
    );
    for (const s of EDIT_SECTIONS) {
      const el = sectionRefs[s.id]?.current;
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  // ── Statutory fields save ─────────────────────────────────────────────────
  async function saveStatutoryFields() {
    setSavingStatutory(true); notifyError("");
    try {
      await api.patch(`/employees/${id}/hr-fields`, {
        esiNumber: esiNumber || null,
        uanNumber: uanNumber || null,
        medicalCard: medicalCard || null,
        pensionStatus: pensionStatus || null,
        appraisalLastDate: appraisalLastDate || null,
        appraisalDueDate: appraisalDueDate || null,
        poNumber: poNumber || null,
      });
      setStatutorySaved(true);
      setStatutoryEditing(false);
      setTimeout(() => setStatutorySaved(false), 2000);
    } catch (e: unknown) {
      const msg = errMsg(e);
      notifyError(msg ?? "Failed to save statutory fields");
      throw e;
    } finally {
      setSavingStatutory(false);
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  // The values the create POST persists, keyed the way handleSave addresses
  // them, so a save that follows a create in the same card can compare.
  function snapshotCreateFields(): Record<string, unknown> {
    return {
      firstName, lastName, employeeType, role, designation,
      departmentId: departmentId || null, officeId: officeId || null,
      managerId: managerId || null,
      reportingManagerId: (sameAsHR ? managerId : reportingManagerId) || null,
      workLocation, hireDate: hireDate || null, probationEndDate: probationEndDate || null,
      noticePeriodDays: noticePeriodDays ? Number(noticePeriodDays) : null,
      currency, costCentre, baseSalary: baseSalary ? Number(baseSalary) : null,
      personalEmail: personalEmail || null,
    };
  }

  async function handleSave() {
    if (readOnly) return;
    setSaving(true);
    notifyError("");
    // Before ensureEmployee(), so a create-mode save does not create the
    // employee and only then refuse the rest of the form.
    if (ecDuplicatePhone) {
      notifyError(DUPLICATE_PHONE_MESSAGE);
      setSaving(false);
      throw new Error("validation");
    }
    const justCreated = isCreate && !employeeId;
    const eid = employeeId ?? await ensureEmployee();
    if (!eid) {
      // When the fields are filled, ensureEmployee already set the server's reason
      // (e.g. "Employee code INT-IN-997 is already in use"); don't overwrite it.
      if (!employeeCode.trim() || !firstName.trim() || !email.trim()) {
        notifyError("Could not create employee. Fill in Employee Code, First Name and Email.");
      }
      setSaving(false);
      throw new Error("validation");
    }
    try {
      const directFields: Record<string, unknown> = {};
      const approvalFields: Array<{ field: string; value: unknown }> = [];

      const addField = (field: string, value: unknown) => {
        if (isCreate) {
          if (justCreated && FIELDS_SET_BY_CREATE.has(field)) return;
          // A second save from the created card: the create POST already stored
          // these, so only send the ones HR has since changed.
          const snapshot = createdFieldValues.current;
          if (snapshot && FIELDS_SET_BY_CREATE.has(field) && snapshot[field] === value) return;
          if (value !== "" && value !== null && value !== undefined) directFields[field] = value;
          return;
        }
        const p = permissions[field];
        if (!p || p.canViewInProfile !== "FULL" || p.canEdit === "NONE" || p.isLocked) return;
        if (p.canEdit === "DIRECT") {
          if (value !== "" && value !== null && value !== undefined) directFields[field] = value;
          else directFields[field] = null;
        } else if (p.canEdit === "WITH_APPROVAL") {
          approvalFields.push({ field, value });
        }
      };

      addField("firstName",        firstName);
      addField("middleName",       middleName || null);
      addField("lastName",         lastName);
      addField("employeeType",     employeeType);
      addField("role",             role);
      addField("designation",      designation);
      addField("departmentId",     departmentId || null);
      addField("officeId",         officeId || null);
      addField("managerId",          managerId || null);
      addField("reportingManagerId", (sameAsHR ? managerId : reportingManagerId) || null);
      addField("workLocation",     workLocation);
      addField("hireDate",         hireDate || null);
      addField("probationEndDate", probationEndDate || null);
      addField("title",            title || null);
      addField("dateOfBirth",      dateOfBirth || null);
      addField("gender",           gender || null);
      addField("maritalStatus",    maritalStatus || null);
      addField("bloodGroup",       bloodGroup || null);
      addField("personalEmail",    personalEmail || null);
      addField("personalPhone",    personalPhone || null);
      addField("linkedIn",         linkedIn || null);
      addField("panNumber",        panNumber || null);
      addField("aadhaarNumber",    aadhaarNumber || null);
      addField("residentialAddress",      residentialAddress || null);
      addField("residentialAddressLine2", residentialAddressLine2 || null);
      addField("residentialCity",         residentialCity || null);
      addField("residentialState",        residentialState || null);
      addField("residentialDistrict",     residentialDistrict || null);
      addField("residentialPincode",      residentialPincode || null);
      addField("residentialLat",          residentialLat);
      addField("residentialLng",          residentialLng);
      addField("permanentAddress",        permanentAddress || null);
      addField("permanentAddressLine2",   permanentAddressLine2 || null);
      addField("permanentCity",           permanentCity || null);
      addField("permanentState",          permanentState || null);
      addField("permanentDistrict",       permanentDistrict || null);
      addField("permanentPincode",        permanentPincode || null);
      addField("permanentLat",            permanentLat);
      addField("permanentLng",            permanentLng);
      addField("currency",         currency);
      addField("noticePeriodDays", noticePeriodDays ? parseInt(noticePeriodDays, 10) : null);
      addField("costCentre",       costCentre || null);
      addField("baseSalary",       baseSalary ? parseFloat(baseSalary) : null);
      addField("bankAccountNumber",    bankAccountNumber || null);
      addField("bankIfscCode",         bankIfscCode || null);
      addField("bankName",             bankName || null);
      addField("bankAccountHolderName",bankAccountHolderName || null);
      addField("emergencyContact1Name",     ec1Name || null);
      addField("emergencyContact1Phone",    ec1Phone || null);
      addField("emergencyContact1Relation", ec1Relation || null);
      addField("emergencyContact2Name",     ec2Name || null);
      addField("emergencyContact2Phone",    ec2Phone || null);
      addField("emergencyContact2Relation", ec2Relation || null);

      const requests: Promise<unknown>[] = [];

      if (Object.keys(directFields).length > 0) {
        requests.push(api.patch(`/employees/${eid}`, directFields));
      }

      for (const { field, value } of approvalFields) {
        requests.push(api.post(`/employees/${eid}/field-change-request`, { field, proposedValue: value }));
      }

      const validEdu = education.filter((e) => e.degree && e.institution);
      const validWork = workHistory.filter((w) => w.company && w.title);
      if (validEdu.length > 0 || validWork.length > 0 || skills) {
        requests.push(api.patch(`/employees/${eid}/skills-history`, {
          skills: skills || undefined,
          education: validEdu.map((e) => ({
            degree: e.degree, institution: e.institution,
            fieldOfStudy: e.fieldOfStudy || undefined,
            startYear: e.startYear ? parseInt(e.startYear, 10) : undefined,
            endYear: e.endYear ? parseInt(e.endYear, 10) : undefined,
          })),
          workHistory: validWork.map((w) => ({
            company: w.company, title: w.title,
            startDate: w.startDate || undefined,
            endDate: w.isCurrent ? undefined : (w.endDate || undefined),
            isCurrent: w.isCurrent,
          })),
        }).catch((err) => logError("EmployeeProfilePage.prefillWizardState", err)));
      }

      await Promise.all(requests);

      const savedMsg = approvalFields.length > 0
        ? (Object.keys(directFields).length > 0 ? "Changes saved — some fields sent for approval." : "Change(s) submitted for approval.")
        : (isCreate ? "Employee created." : "Changes saved.");
      toast.show(savedMsg, "success");

      // Read back what the server actually stored. Until this was added the page
      // issued the PATCH and never re-read the record, so it kept rendering
      // whatever had been typed: the status pill, employeeCode, any value the
      // server normalises, and approval-gated fields the server did NOT apply
      // directly all held their pre-save value until a manual page reload.
      // This replaces two earlier one-off refreshes bolted on for single
      // symptoms (the onboarding emergency-contact badge, which derives from
      // onboardingRecord.emergencyContacts rather than item.isCompleted, and the
      // "Pending approval" badge read from permissions[field].hasPendingChange).
      // allSettled, so one failing refresh cannot turn a successful save into an
      // error. onboardingRecord stays guarded: employees without a record would
      // 404 on every save.
      // One reload covers every slice: loadEmployee() re-reads the record,
      // field-permissions, documents, compensation, shift, assets, skills and
      // (unless skipped) onboarding. Listing those separately here only doubled
      // the requests. skipOnboarding mirrors the guard this replaced: employees
      // without a record should not 404 on every save.
      if (!isCreate) await loadEmployee({ skipOnboarding: !onboardingRecord });

      if (isCreate) {
        await Promise.allSettled([
          pendingResumeFile  ? (vhr.isDocEnabled("OTHER")
            ? uploadDocumentWithAI(eid, pendingResumeFile, "OTHER", "RESUME")
            : uploadDocumentOnly(eid, pendingResumeFile, "OTHER", { skipVerify: true })
          ).catch((err) => logError("EmployeeProfilePage.uploadPendingResume", err)) : null,
          // The offer letter was already parsed from the picked file, so its
          // fields are attached to the stored version rather than paying for a
          // second extraction — which would also fail for a Word file, since the
          // stored-document path cannot read those.
          pendingOfferLetterFile
            ? uploadDocumentOnly(eid, pendingOfferLetterFile, "OFFER_LETTER")
                .then(({ versionId }) => (offerLetterFields ? persistExtractedData(versionId, offerLetterFields) : undefined))
                .catch((err) => logError("EmployeeProfilePage.uploadPendingOfferLetter", err))
            : null,
        ].filter(Boolean));
        // Cleared so a second save from the created card does not re-upload them.
        setPendingResumeFile(null);
        setPendingOfferLetterFile(null);

        // Stay on the card rather than navigating to the edit page: the next
        // thing HR does is send the onboarding link, and that now lives in this
        // same card. Snapshot the fields the create POST set so a later save
        // from here re-sends only the ones that actually changed — re-sending
        // all of them goes through the edit-permission path and 422s for roles
        // that may create an employee but not directly edit those fields.
        createdFieldValues.current = snapshotCreateFields();
        setCreated({ employeeId: eid, employeeCode: "" });
        api.get<{ employeeCode: string }>(`/employees/${eid}`)
          .then(r => setCreated({ employeeId: eid, employeeCode: r.data.employeeCode }))
          .catch((err) => logError("EmployeeProfilePage.loadCreatedEmployee", err, { eid }));
        void refreshOnboarding(eid);
      }
    } catch (err: unknown) {
      const msg = errMsg(err);
      notifyError(msg ?? "Save failed. Please try again.");
      toast.show(msg ?? "Save failed. Please try again.", "error");
      throw err;
    } finally {
      setSaving(false);
    }
  }

  const pageTitle = isCreate
    ? "Add Employee"
    : (firstName || lastName ? [firstName, middleName, lastName].filter(Boolean).join(" ") : "Edit Employee");

  // ── Section nav helpers ────────────────────────────────────────────────────
  function editSectionStatus(id: EditSectionId): SectionStatus {
    switch (id) {
      case "documents": {
        const allVerified = REQUIRED_DOC_TYPES.every(({ aliases }) => docs.find(d => (aliases as readonly string[]).includes(d.type))?.status === "VERIFIED");
        return allVerified ? "complete" : hasAnyDoc ? "partial" : "empty";
      }
      case "identity":     return (firstName && lastName) ? "complete" : (firstName || lastName) ? "partial" : "empty";
      // In create mode this card carries the whole hire, not just the org fields.
      case "role":         return isCreate
        ? ((firstName && lastName && email && designation && officeId && departmentId && hireDate) ? "complete"
          : (firstName || designation) ? "partial" : "empty")
        : (designation && departmentId) ? "complete" : (designation || departmentId) ? "partial" : "empty";
      case "personal":     return (dateOfBirth && gender && personalEmail) ? "complete" : (dateOfBirth || gender || personalEmail) ? "partial" : "empty";
      case "address":      return (residentialAddress && residentialCity && residentialState) ? "complete" : residentialAddress ? "partial" : "empty";
      case "compensation": return (baseSalary || compSnapshot) ? "complete" : "empty";
      case "schedule":     return currentShift ? "complete" : "empty";
      case "leave":        return "complete";
      case "bank":         return (bankAccountNumber && bankIfscCode) ? "complete" : bankAccountNumber ? "partial" : "empty";
      case "contacts":     return (ec1Name && ec1Phone && ec2Name && ec2Phone) ? "complete" : (ec1Name || ec2Name) ? "partial" : "empty";
      case "skills":       return (skills && (education[0]?.degree || workHistory[0]?.company)) ? "complete" : (skills || education[0]?.degree) ? "partial" : "empty";
      case "history":      return "complete";
      case "onboarding":   return onboardingRecord?.status === "COMPLETED" ? "complete" : onboardingRecord ? "partial" : "empty";
    }
  }

  // ── Name / DOB mismatch detection ────────────────────────────────────────
  function normalizeName(n: string) {
    return n.toLowerCase().trim().replace(/\s+/g, " ");
  }
  function nameMatch(a: string, b: string): boolean {
    const na = normalizeName(a); const nb = normalizeName(b);
    if (na === nb) return true;
    const pa = na.split(" "); const pb = nb.split(" ");
    return pa[0] === pb[0] && pa[pa.length - 1] === pb[pb.length - 1];
  }
  // Compares whatever names the create-mode documents yielded — offer letter,
  // résumé, cancelled cheque. Identity documents no longer feed this: the
  // employee uploads those through the wizard, so the "critical" Aadhaar-vs-PAN
  // case cannot arise here and every disagreement is a warning HR resolves by
  // picking the right name.
  const nameMismatch = (() => {
    const entries = Object.entries(ocrName).filter(([, v]) => v.trim());
    if (entries.length < 2) return null;
    const disagrees = entries.some(([, a], i) => entries.slice(i + 1).some(([, b]) => !nameMatch(a, b)));
    if (!disagrees) return null;
    return { sources: entries };
  })();

  // Create mode has no Documents, Identity, Change History or Onboarding card to
  // scroll to — all four render only for a saved employee — so they must not sit
  // in the nav as links that do nothing.
  const CREATE_SECTION_IDS = new Set<string>(["role", "personal", "address", "compensation", "schedule", "bank", "contacts", "skills"]);
  const visibleEditSections = isCreate
    // "Role & Org" would not name what this card is in create mode — it carries
    // the whole hire, and the card itself says "Start here".
    ? EDIT_SECTIONS.filter(s => CREATE_SECTION_IDS.has(s.id)).map(s => (s.id === "role" ? { ...s, label: "Start here" } : s))
    : EDIT_SECTIONS.filter(s =>
    (s.id !== "onboarding" || isHR) && (s.id !== "leave" || canViewLeaveTab)
  );

  const editNavSections: NavSection[] = visibleEditSections.map(s => ({
    id: s.id, label: s.label, icon: s.icon, status: editSectionStatus(s.id),
  }));

  const editHRStatuses: HRSectionStatus[] = visibleEditSections.map(s => ({
    id: s.id, label: s.label, status: editSectionStatus(s.id),
  }));

  // Derived from what is actually on screen, so the create-mode percentage does
  // not count sections that mode never renders.
  const COMPLETION_SECTIONS = visibleEditSections.filter(s => s.id !== "history" && s.id !== "onboarding" && s.id !== "leave");
  const editCompletionPct = Math.round(
    (COMPLETION_SECTIONS.filter(s => editSectionStatus(s.id) !== "empty").length / COMPLETION_SECTIONS.length) * 100
  );

  function scrollToSection(sid: string) {
    sectionCollapse.open(sid);
    setPendingScroll(sid as EditSectionId);
  }

  useEffect(() => {
    if (!pendingScroll) return;
    sectionRefs[pendingScroll]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScroll(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScroll]);

  // Checklist items are tracked as their own DB flag, but several of them duplicate
  // state that already lives (and is already live/authoritative) elsewhere on this
  // page -- deriving from that directly avoids the checklist flag going stale
  // whenever some upload/verify/lock path forgets to also update it.
  function isChecklistItemDone(item: { title: string; isCompleted: boolean }): boolean {
    if (item.title === "Background verification completed") return onboardingRecord?.bgvStatus === "VERIFIED";
    if (item.title === "Compensation record locked") return !!onboardingRecord?.compensationLocked;
    if (item.title === "Acknowledge employee handbook") return !!onboardingRecord?.handbookAckedAt;
    if (item.title === "Add emergency contacts (min 2)") return (onboardingRecord?.emergencyContacts?.length ?? 0) >= 2;
    if (item.title === "Data-protection notice accepted") return !!onboardingRecord?.dataConsentAt;
    if (item.title === "Emergency contacts verified by HR") return verifiedContactCount() >= 2;
    if (item.title === "References verified") return (onboardingRecord?.references?.filter(r => r.status === "VERIFIED").length ?? 0) >= 2;
    if (item.title === "AD account provisioned") return adStatus?.adAccountStatus === "ACTIVE";
    const docType = CHECKLIST_DOC_TITLES[item.title];
    if (docType) return !!docs.find(d => (docType.aliases as readonly string[]).includes(d.type) && d.status === "VERIFIED");
    return item.isCompleted;
  }

  useEffect(() => {
    const target = searchParams.get("section");
    if (target === "onboarding" && onboardingRecord) scrollToSection("onboarding");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, onboardingRecord]);

  useEffect(() => {
    if (!id || !isHR || !onboardingRecord?.handbookAckedAt) { setSignedHandbook(null); return; }
    api.get<typeof signedHandbook>(`/employees/${id}/handbook/signed`)
      .then(r => setSignedHandbook(r.data))
      .catch(() => setSignedHandbook(null));
  }, [id, isHR, onboardingRecord?.handbookAckedAt]);

  // ── Onboarding section actions ─────────────────────────────────────────────
  // Takes an explicit id for create mode, where the route has none but the hire
  // the card just created does.
  function refreshOnboarding(employeeIdOverride?: string) {
    const target = employeeIdOverride ?? id;
    if (!target) return Promise.resolve();
    return api.get<typeof onboardingRecord>(`/employees/${target}/onboarding`)
      .then(r => setOnboardingRecord(r.data))
      .catch(() => {});
  }

  // Re-reads only the server-owned header fields, so it is safe to call while
  // the user has unsaved edits open -- unlike loadEmployee(), which re-hydrates
  // every input on the page and would discard them.
  function refreshEmployeeHeader() {
    if (!id) return Promise.resolve();
    return api.get<Record<string, unknown>>(`/employees/${id}`)
      .then(r => {
        setStatus(String(r.data["status"] ?? ""));
        setEmployeeCode(String(r.data["employeeCode"] ?? ""));
      })
      .catch(() => {});
  }

  function verifiedContactCount(): number {
    return onboardingRecord?.emergencyContacts.filter(c => c.verificationStatus === "VERIFIED").length ?? 0;
  }

  // Mirrors the server's activation gates (checkGatesAndActivate) so the button
  // can say what is still missing instead of failing with a 422.
  function getOnboardingGateIssues(): string[] {
    if (!onboardingRecord) return [];
    const issues: string[] = [];
    const unverifiedDocs = REQUIRED_DOC_TYPES
      .filter(({ aliases }) => docs.find(d => (aliases as readonly string[]).includes(d.type))?.status !== "VERIFIED")
      .map(d => d.label);
    if (unverifiedDocs.length) issues.push(`${unverifiedDocs.join(", ")} not yet verified by HR`);
    if (!onboardingRecord.dataConsentAt) issues.push("data-protection notice not accepted");
    if (onboardingRecord.bgvStatus !== "VERIFIED") issues.push("background verification not complete");
    if (verifiedContactCount() < 2) issues.push("2 emergency contacts not yet verified by HR");
    if (onboardingRecord.references.filter(r => r.status === "VERIFIED").length < 2) issues.push("2 references not yet verified by HR");
    if (!onboardingRecord.handbookAckedAt) issues.push("employee handbook not acknowledged");
    if (!onboardingRecord.compensationLocked) issues.push("compensation not locked");
    return issues;
  }

  async function initiateOnboarding() {
    if (!id) return;
    setInitiatingOnboarding(true); notifyError(""); notifySuccess("");
    try {
      await api.post(`/employees/${id}/onboarding`);
      notifySuccess("Onboarding initiated.");
      refreshOnboarding();
    } catch (e: unknown) {
      notifyError(errMsg(e) ?? "Failed to initiate onboarding.");
      throw e;
    } finally {
      setInitiatingOnboarding(false);
    }
  }

  async function recheckDoc(docId: string) {
    setRecheckingDocId(docId); notifyError("");
    try {
      const { data } = await api.patch<{ document: { id: string; type: string; status: string }; found: boolean }>(
        `/documents/${docId}/recheck-upload`
      );
      void loadDocs();
      if (!data.found) notifyError("File was never found in storage — please re-upload this document.");
    } catch (e: unknown) {
      notifyError(errMsg(e) ?? "Failed to recheck upload");
      throw e;
    } finally {
      setRecheckingDocId(null);
    }
  }

  async function toggleChecklistItem(itemId: string, isCompleted: boolean) {
    setTogglingItemId(itemId); notifyError("");
    try {
      await api.patch(`/onboarding/checklist/${itemId}`, { isCompleted });
      refreshOnboarding();
    } catch (e: unknown) { notifyError(errMsg(e) ?? "Failed to update checklist item"); }
    setTogglingItemId(null);
  }


  // Locking happens when Finance approves a revision; HR can only unlock, with a logged reason.
  async function unlockCompensation(reason: string) {
    if (!id) return;
    setLocking(true); notifyError("");
    try {
      await api.post(`/employees/${id}/onboarding/lock-compensation`, { lock: false, reason });
      notifySuccess("Compensation unlocked. Submit a new revision for Finance approval.");
      refreshOnboarding();
      api.get<CompHistoryItem[]>(`/employees/${id}/compensation/snapshots`)
        .then(r => setCompHistory(r.data))
        .catch((err) => logError("EmployeeProfilePage.reloadCompHistory", err, { employeeId: id }));
    } catch (e: unknown) {
      notifyError(errMsg(e) ?? "Failed to unlock compensation");
      throw e;
    } finally {
      setLocking(false);
    }
  }

  async function createAdAccount(confirmExisting = false) {
    if (!id) return;
    setAdCreating(true); notifyError(""); setAdResult(null);
    try {
      const { data } = await api.post<{
        adObjectId?: string; adAccountStatus?: string; tempPassword?: string;
        tempPasswordSentTo?: string | null; exists?: boolean; upn?: string;
      }>(`/employees/${id}/ad-account`, confirmExisting ? { confirmExisting: true } : {});
      if (data.exists) { setAdExistsPrompt({ upn: data.upn! }); return; }
      setAdResult(data.tempPassword ? { tempPassword: data.tempPassword, tempPasswordSentTo: data.tempPasswordSentTo ?? null } : null);
      setAdStatus({ adAccountStatus: data.adAccountStatus!, adLastError: null });
      setAdExistsPrompt(null);
    } catch (e: unknown) {
      notifyError(errMsg(e) ?? "Failed to create AD account");
      throw e;
    } finally {
      setAdCreating(false); setAdConfirmOpen(false);
    }
  }

  async function requestHandbookSignature() {
    if (!id) return;
    // Opened synchronously (before the await below) so it stays tied to this click's
    // user-gesture — popup blockers reject window.open() called after an await.
    const popup = window.open("", "handbook-sign", "width=760,height=880,resizable=yes,scrollbars=yes");
    setHrAcking(true); notifyError(""); notifySuccess(""); setSignLink(null); setLinkCopied(false);
    try {
      const { data } = await api.post<{
        sentTo: string; signUrl: string; expiresAt: string; emailed: boolean; emailError: string | null;
      }>(`/employees/${id}/handbook/request-signature`, {});
      setSignLink({
        url: data.signUrl, expiresAt: data.expiresAt,
        emailed: data.emailed, emailError: data.emailError, sentTo: data.sentTo,
      });
      if (popup && !popup.closed) popup.location.href = data.signUrl;
    } catch (e: unknown) {
      popup?.close();
      notifyError(errMsg(e) ?? "Failed to generate signing link");
      throw e;
    } finally {
      setHrAcking(false);
    }
  }

  function openSignPopup() {
    if (!signLink) return;
    window.open(signLink.url, "handbook-sign", "width=760,height=880,resizable=yes,scrollbars=yes");
  }

  async function copySignLink() {
    if (!signLink) return;
    try {
      await navigator.clipboard.writeText(signLink.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { notifyError("Couldn't copy automatically — select the link and copy it manually."); }
  }

  async function confirmReadyForPayroll() {
    if (!id || !onboardingRecord) return;
    setPayrollChecking(true); setPayrollResult(null); notifyError(""); notifySuccess("");
    try {
      const { data } = await api.get<{ ready: boolean; system: string; missing: string[]; message: string }>(
        `/employees/${id}/onboarding/payroll-ready`
      );
      setPayrollResult(data);
      if (!data.ready) { setPayrollChecking(false); return; }

      const issues = getOnboardingGateIssues();
      if (issues.length > 0) {
        const gateMsg = `Onboarding can't be completed yet — ${issues.join("; ")}.`;
        notifyError(gateMsg);
        toast.show(gateMsg, "warning");
        setPayrollChecking(false); return;
      }

      setPayrollChecking(false);
      setActivating(true);
      await api.post(`/employees/${id}/activate`, {});
      notifySuccess("Employee successfully activated!");
      toast.show("Employee successfully activated!", "success");
      refreshOnboarding();
      // Activation flips employee.status, which lives in the record this page
      // otherwise reads only on mount, so the ACTIVE pill stayed stale. Header
      // refresh rather than loadEmployee(): Activate can be clicked with unsaved
      // edits open in another section, and a full re-hydrate would discard them.
      refreshEmployeeHeader();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { details?: string[] } } };
      const msg = errMsg(e) ?? "Failed";
      const details = err?.response?.data?.details;
      const activateErrMsg = details ? `${msg}: ${details.join(", ")}` : msg;
      notifyError(activateErrMsg);
      toast.show(activateErrMsg, "error");
      throw e;
    } finally {
      setPayrollChecking(false); setActivating(false);
    }
  }

  // ── Guard ─────────────────────────────────────────────────────────────────
  // Kept below every hook: an early return above one breaks the rules of hooks.
  if (!isHR && isCreate) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <p style={{ color: "var(--color-surface-700)" }}>You don't have permission to create employees.</p>
      </div>
    );
  }

  const deptName   = orgMeta.departments.find(d => d.id === departmentId)?.name ?? "";
  const officeName = orgMeta.offices.find(o => o.id === officeId)?.name ?? "";
  const selectedOfficeCountry = (orgMeta.offices.find(o => o.id === officeId)?.country ?? "").toUpperCase();
  const isUSEmployee = !isCreate && selectedOfficeCountry === "US";

  return (
    <div style={{ height: "100%", background: "var(--color-surface-50)" }}>
      <EmployeeFormShell
        leftNav={
          <>
            <SectionAnchorNav
              sections={editNavSections}
              activeId={activeSection}
              onSectionClick={scrollToSection}
            />
          </>
        }
        centerHeader={
          <>
            {/* Name, code, status — confined to the center column's width */}
            <div style={{
              background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
              // Same 60px box as EmployeeMiniProfileBar, so the rule under this row and
              // the rule under the profile beside it are the same line. Vertical padding
              // would make the height depend on the tallest badge, which is what pushed
              // the two rules apart; minHeight still lets it grow if the row wraps.
              minHeight: 60, boxSizing: "border-box",
              // Padding, not a narrower column, so the row's surface and rule still reach
              // the window edge while its controls stay clear of the drawer handle.
              padding: "0 calc(24px + var(--hr-rail, 0px)) 0 24px",
              transition: "padding-right 300ms ease-out",
              display: "flex", alignItems: "center", gap: 16,
            }}>
              {/* The identity group owns all the wrapping. Keeping it a single
                  shrinkable flex item is what stops a long name or badge from
                  pushing the collapse-all toggle onto a second row. */}
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
                <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{pageTitle}</h1>
                {readOnly && <Badge tone="outline">View only</Badge>}
                {employeeCode && (
                  <span style={{ fontSize: 12, color: "var(--color-surface-400)", fontFamily: "monospace" }}>{employeeCode}</span>
                )}
                {!isCreate && status && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 999, whiteSpace: "nowrap",
                    ...({
                      ONBOARDING:    { background: "var(--color-info-bg)", color: "var(--color-info-fg)" },
                      ACTIVE:        { background: "var(--color-success-bg)", color: "var(--color-success-fg)" },
                      ON_LEAVE:      { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" },
                      NOTICE_PERIOD: { background: "var(--color-warning-bg-subtle)", color: "var(--color-warning-fg-strong)" },
                      TERMINATED:    { background: "var(--color-error-bg)", color: "var(--color-error-fg)" },
                      CONTRACTOR:    { background: "var(--color-surface-100)", color: "var(--color-surface-600)" },
                      INTERN:        { background: "var(--color-surface-100)", color: "var(--color-surface-600)" },
                    } as Record<string, React.CSSProperties>)[status] ?? {},
                  }}>
                    {status.replace(/_/g, " ")}
                  </span>
                )}
                {!isCreate && id && <EmployeeProbationBadge employeeId={id} />}
              </div>

              {/* One toggle for every section card: it offers whichever action
                  isn't already the state of the page. `shrink-0` instead of
                  `ml-auto` — the group above already takes the slack, and
                  auto-margin let the button wrap away from the row. */}
              <Button
                variant="secondary"
                size="sm"
                className="w-8 shrink-0 px-0"
                onClick={() => sectionCollapse.setAll(!sectionCollapse.allOpen)}
                title={sectionCollapse.allOpen ? "Collapse all sections" : "Expand all sections"}
                aria-label={sectionCollapse.allOpen ? "Collapse all sections" : "Expand all sections"}
              >
                {sectionCollapse.allOpen ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
              </Button>
              {/* Hide all / view all: the same idea for every card's values. */}
              <Button
                variant="secondary"
                size="sm"
                className="w-8 shrink-0 px-0"
                onClick={() => sectionPrivacy.setAll(sectionPrivacy.allVisible)}
                title={sectionPrivacy.allVisible ? "Hide all details" : "View all details"}
                aria-label={sectionPrivacy.allVisible ? "Hide all details" : "View all details"}
              >
                {sectionPrivacy.allVisible ? <EyeOff size={14} /> : <Eye size={14} />}
              </Button>
            </div>

            {sourceSystem === "ADP" && (
              <div style={{
                padding: "10px 16px", fontSize: 13,
                background: "var(--color-info-bg-subtle)", color: "var(--color-info-fg)",
                borderBottom: "1px solid var(--color-info-border)",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ display: "inline-flex" }}><Link2 size={14} /></span>
                <span>
                  This employee is managed in ADP. Personal, address, salary, and bank fields are read-only here.
                  Only HRMS-specific fields (role, manager, department, office) can be edited.
                </span>
              </div>
            )}
          </>
        }
        rightPanel={
          <>
            {/* Mini profile: avatar, name, designation, dept · office — above Profile completion */}
            <EmployeeMiniProfileBar
              firstName={firstName} middleName={middleName} lastName={lastName}
              designation={designation} dept={deptName} office={officeName}
            />
            <HRReviewPanel
              completionPct={editCompletionPct}
              sectionStatuses={editHRStatuses}
              notes="" onNotesChange={() => {}}
              onSectionClick={scrollToSection}
              onSubmit={handleSave}
              submitLabel="Save Changes"
              submitDisabled={saving}
              saving={saving}
              readOnly={readOnly}
              onCancel={() => navigate("/employees")}
            />
          </>
        }
      >
        <SectionCollapseProvider value={sectionCollapse}>
        <SectionPrivacyProvider value={sectionPrivacy}>
        <div ref={centerRef} style={{ padding: "14px 14px" }}>

          {!isCreate && (
            <DocumentsOverviewStrip
              docs={docs}
              onOpen={openDocFromStrip}
            />
          )}

          {/* View-only: each section disables its own body (see CollapsibleSection),
              so the open/close toggles keep working. The documents strip above
              is outside every section, so files can still be opened. */}
          <SectionReadOnlyProvider value={readOnly}>

          {/* ── Section 1: Documents ──────────────────────────────────────── */}
          <DocumentsSection
            sectionRef={sectionRefs.documents}
            isCreate={isCreate} isUSEmployee={isUSEmployee} permissions={permissions}
            employeeId={employeeId} isHR={isHR} docRefreshKeys={docRefreshKeys}
            setVerifyTarget={setVerifyTarget} bumpDocRefresh={bumpDocRefresh}
            docs={docs} onOpenDoc={openDocFromStrip}
            dateOfBirth={dateOfBirth} setDateOfBirth={setDateOfBirth}
            gender={gender} setGender={setGender}
            setFirstName={setFirstName} setMiddleName={setMiddleName} setLastName={setLastName}
            setPanNumber={setPanNumber} setAadhaarNumber={setAadhaarNumber}
            setUanNumber={setUanNumber}
            applyResumeFields={applyResumeFields}
          />

          {/* ── Assigned Assets (edit mode only) ──────────────────────────── */}
          {!isCreate && <AssignedAssetsSection assets={empAssets} />}

          {/* ── Section 2: Identity ───────────────────────────────────────── */}
          {!isCreate && (
            <IdentitySection
              sectionRef={sectionRefs.identity}
              canView={canView}
              inputProps={inputProps}
              pendingBadge={pendingBadge}
              title={title} setTitle={setTitle}
              firstName={firstName} setFirstName={setFirstName}
              middleName={middleName} setMiddleName={setMiddleName}
              lastName={lastName} setLastName={setLastName}
              email={email}
              employeeCode={employeeCode}
            />
          )}

          {/* ── Section 3: Organization & Role ───────────────────────────── */}
          <RoleOrgSection
            sectionRef={sectionRefs.role}
            canView={canView} inputProps={inputProps} datePickerProps={datePickerProps}
            approvalNote={approvalNote} pendingBadge={pendingBadge} isCreate={isCreate} isHR={isHR}
            employeeType={employeeType} setEmployeeType={setEmployeeType} employeeTypes={EMPLOYEE_TYPES}
            role={role} setRole={setRole} roles={ROLES}
            designation={designation} setDesignation={setDesignation}
            officeId={officeId} setOfficeId={setOfficeId}
            departmentId={departmentId} setDepartmentId={setDepartmentId}
            orgMeta={orgMeta}
            managerId={managerId} setManagerId={setManagerId}
            reportingManagerId={reportingManagerId} setReportingManagerId={setReportingManagerId}
            sameAsHR={sameAsHR} setSameAsHR={setSameAsHR} managers={managers}
            workLocation={workLocation} setWorkLocation={setWorkLocation} workLocations={workLocations}
            hireDate={hireDate} setHireDate={setHireDate}
            probationEndDate={probationEndDate} setProbationEndDate={setProbationEndDateTouched}
            firstName={firstName} setFirstName={setFirstName}
            lastName={lastName} setLastName={setLastName}
            setMiddleName={setMiddleName}
            email={email} setEmail={setEmail}
            employeeCode={employeeCode} setEmployeeCode={setEmployeeCode}
            emailSuggested={emailSuggested} setEmailSuggested={setEmailSuggested}
            nameMismatch={nameMismatch}
            ackedConflicts={ackedConflicts} setAckedConflicts={setAckedConflicts}
            employeeId={employeeId} docRefreshKeys={docRefreshKeys}
            setVerifyTarget={setVerifyTarget} bumpDocRefresh={bumpDocRefresh}
            // HRMS-0633: edit-mode offer-letter re-upload runs the AI (previously
            // uploadDocumentOnly) and bubbles the extracted fields up so the
            // comp card's prefill picks up the new annual CTC.
            onOfferLetterParsed={(fields) => setOfferLetterFields(fields)}
            {...(isCreate ? { intake: {
              offerLetter: {
                fileName: attachedNames.offerLetter,
                loading: offerLetterLoading,
                message: offerLetterMsg,
                onFile: (f: File) => { void handleOfferLetterFile(f); },
              },
              resume: {
                fileName: attachedNames.resume,
                loading: resumeLoading,
                message: resumeMsg,
                onFile: (f: File) => { void handleResumeFile(f); },
              },
              ctcNote: offerLetterCtcNote,
              personalEmail, setPersonalEmail,
              missing: [
                !employeeCode.trim() && "Employee Code",
                !firstName.trim() && "First Name",
                !lastName.trim() && "Last Name",
                !email.trim() && "Work Email",
              ].filter((v): v is string => typeof v === "string"),
              onSave: handleSave,
              created: created && {
                employeeId: created.employeeId,
                employeeCode: created.employeeCode,
                record: onboardingRecord,
                onLinkSent: () => refreshOnboarding(created.employeeId),
                onOpenProfile: () => navigate(`/employees/${created.employeeId}/edit`),
              },
            } } : {})}
          />

          {/* ── Section 4: Personal Info ─────────────────────────────────── */}
          <PersonalInfoSection
            sectionRef={sectionRefs.personal}
            canView={canView} inputProps={inputProps} datePickerProps={datePickerProps}
            approvalNote={approvalNote} pendingBadge={pendingBadge}
            isCreate={isCreate} isUSEmployee={isUSEmployee} isHR={isHR}
            selectedOfficeCountry={selectedOfficeCountry}
            genders={genders} maritalStatuses={maritalStatuses} bloodGroups={bloodGroups}
            isEmailInvalid={isEmailInvalid} panInvalid={panInvalid} aadhaarInvalid={aadhaarInvalid}
            dateOfBirth={dateOfBirth} setDateOfBirth={setDateOfBirth}
            gender={gender} setGender={setGender}
            maritalStatus={maritalStatus} setMaritalStatus={setMaritalStatus}
            bloodGroup={bloodGroup} setBloodGroup={setBloodGroup}
            personalEmail={personalEmail} setPersonalEmail={setPersonalEmail}
            personalPhone={personalPhone} setPersonalPhone={setPersonalPhone}
            linkedIn={linkedIn} setLinkedIn={setLinkedIn}
            panNumber={panNumber} setPanNumber={setPanNumber}
            aadhaarNumber={aadhaarNumber} setAadhaarNumber={setAadhaarNumber}
          />

          {/* ── Section 5: Address ────────────────────────────────────────── */}
          {(canView("residentialAddress") || canView("permanentAddress")) && (
            <AddressSection
              sectionRef={sectionRefs.address}
              canView={canView} inputProps={inputProps} pendingBadge={pendingBadge}
              isCreate={isCreate} permissions={permissions}
              selectedOfficeCountry={selectedOfficeCountry} isPincodeInvalid={isPincodeInvalid}
              permanentAddress={permanentAddress} setPermanentAddress={setPermanentAddress}
              permanentAddressLine2={permanentAddressLine2} setPermanentAddressLine2={setPermanentAddressLine2}
              permanentCity={permanentCity} setPermanentCity={setPermanentCity}
              permanentState={permanentState} setPermanentState={setPermanentState}
              permanentDistrict={permanentDistrict} setPermanentDistrict={setPermanentDistrict}
              permanentPincode={permanentPincode} setPermanentPincode={setPermanentPincode}
              setPermanentLat={setPermanentLat} setPermanentLng={setPermanentLng}
              residentialAddress={residentialAddress} setResidentialAddress={setResidentialAddress}
              residentialAddressLine2={residentialAddressLine2} setResidentialAddressLine2={setResidentialAddressLine2}
              residentialCity={residentialCity} setResidentialCity={setResidentialCity}
              residentialState={residentialState} setResidentialState={setResidentialState}
              residentialDistrict={residentialDistrict} setResidentialDistrict={setResidentialDistrict}
              residentialPincode={residentialPincode} setResidentialPincode={setResidentialPincode}
              setResidentialLat={setResidentialLat} setResidentialLng={setResidentialLng}
              sameAsPermanent={sameAsPermanent} setSameAsPermanent={setSameAsPermanent}
            />
          )}

          {/* ── Section 6: Compensation & Terms ──────────────────────────── */}
          {(isCreate ? isHR : (canView("baseSalary") || canView("noticePeriodDays") || canView("costCentre"))) && (
            <CompensationSection
              sectionRef={sectionRefs.compensation}
              canView={canView} inputProps={inputProps} isCreate={isCreate} permissions={permissions}
              canEditComp={SALARY_ROLES.has(viewerRole) && !readOnly}
              isCompVerifier={COMP_VERIFY_ROLES.has(viewerRole)}
              employeeId={employeeId} ensureEmployee={ensureEmployee} setSilentError={notifyError}
              employeeType={employeeType} dateOfBirth={dateOfBirth}
              compSnapshot={compSnapshot} setCompSnapshot={setCompSnapshot}
              compHistory={compHistory} setCompHistory={setCompHistory}
              showRevDrawer={showRevDrawer} setShowRevDrawer={setShowRevDrawer}
              historyExpanded={historyExpanded} setHistoryExpanded={setHistoryExpanded}
              noticePeriodDays={noticePeriodDays} setNoticePeriodDays={setNoticePeriodDays}
              costCentre={costCentre} setCostCentre={setCostCentre}
              offerLetterCtc={offerLetterCtc}
            />
          )}

          {/* ── Section 6b: Statutory Information (HR-only) ──────────────── */}
          {!isCreate && canView("esiNumber") && (
            <StatutorySection
              isHR={isHR}
              statutoryEditing={statutoryEditing} setStatutoryEditing={setStatutoryEditing}
              savingStatutory={savingStatutory}
              setStatutoryErr={notifyError}
              statutorySaved={statutorySaved}
              onSave={() => saveStatutoryFields()}
              esiNumber={esiNumber} setEsiNumber={setEsiNumber}
              uanNumber={uanNumber} setUanNumber={setUanNumber}
              medicalCard={medicalCard} setMedicalCard={setMedicalCard}
              pensionStatus={pensionStatus} setPensionStatus={setPensionStatus}
              appraisalLastDate={appraisalLastDate} setAppraisalLastDate={setAppraisalLastDate}
              appraisalDueDate={appraisalDueDate} setAppraisalDueDate={setAppraisalDueDate}
              poNumber={poNumber} setPoNumber={setPoNumber}
            />
          )}

          {/* ── Section 7: Work Schedule ──────────────────────────────────── */}
          <WorkScheduleSection
            sectionRef={sectionRefs.schedule}
            canEditShift={canEditShift} employeeId={employeeId} ensureEmployee={ensureEmployee}
            currentShift={currentShift} setCurrentShift={setCurrentShift}
            shiftHistory={shiftHistory} setShiftHistory={setShiftHistory}
            shiftStartHour={shiftStartHour} setShiftStartHour={setShiftStartHour}
            shiftType={shiftType} setShiftType={setShiftType}
            shiftEffFrom={shiftEffFrom} setShiftEffFrom={setShiftEffFrom}
            shiftReason={shiftReason} setShiftReason={setShiftReason}
            shiftSaving={shiftSaving} setShiftSaving={setShiftSaving}
            shiftError={shiftError} setShiftError={setShiftError}
            shiftHistExpanded={shiftHistExpanded} setShiftHistExpanded={setShiftHistExpanded}
            shiftDragRef={shiftDragRef}
          />

          {/* ── Section: Leave ─────────────────────────────────────────────── */}
          {canViewLeaveTab && (
            <LeaveSection
              sectionRef={sectionRefs.leave}
              loading={leaveLoading}
              balance={leaveBalance}
              leaveTypeLabels={Object.fromEntries(leaveTypes.map(t => [t.value, t.displayName]))}
              requests={leaveRequests}
              typeConfig={leaveTypeConfig}
              gender={gender}
              maritalStatus={maritalStatus}
              hireDate={hireDate}
            />
          )}

          {/* ── Section 7: Bank Details ───────────────────────────────────── */}
          {!isUSEmployee && (isCreate ? isHR : (canView("bankAccountNumber") || canView("bankIfscCode"))) && (
            <BankDetailsSection
              sectionRef={sectionRefs.bank}
              canView={canView} inputProps={inputProps} approvalNote={approvalNote} pendingBadge={pendingBadge}
              isIfscInvalid={isIfscInvalid} isCreate={isCreate} isHR={isHR}
              employeeId={employeeId} ensureEmployee={ensureEmployee} setSilentError={notifyError}
              setOcrName={setOcrName} bumpDocRefresh={bumpDocRefresh} docRefreshKeys={docRefreshKeys} setVerifyTarget={setVerifyTarget}
              bankDocLocked={!isHR && docs.some(d => d.type === "BANK_DOCUMENT" && d.status === "VERIFIED")}
              bankAccountNumber={bankAccountNumber} setBankAccountNumber={setBankAccountNumber}
              bankIfscCode={bankIfscCode} setBankIfscCode={setBankIfscCode}
              bankName={bankName} setBankName={setBankName}
              bankAccountHolderName={bankAccountHolderName} setBankAccountHolderName={setBankAccountHolderName}
            />
          )}

          {/* ── Emergency Contacts ───────────────────────────────────────── */}
          <EmergencyContactsSection
            sectionRef={sectionRefs.contacts}
            inputProps={inputProps}
            duplicatePhone={ecDuplicatePhone}
            ec1Name={ec1Name} setEc1Name={setEc1Name}
            ec1Phone={ec1Phone} setEc1Phone={setEc1Phone}
            ec1Relation={ec1Relation} setEc1Relation={setEc1Relation}
            ec2Name={ec2Name} setEc2Name={setEc2Name}
            ec2Phone={ec2Phone} setEc2Phone={setEc2Phone}
            ec2Relation={ec2Relation} setEc2Relation={setEc2Relation}
          />

          {/* ── Section 8: Skills, Education & Work History ───────────────── */}
          <SkillsHistorySection
            sectionRef={sectionRefs.skills}
            inputProps={inputProps} isCreate={isCreate} permissions={permissions}
            skills={skills} setSkills={setSkills}
            education={education} setEducation={setEducation}
            workHistory={workHistory} setWorkHistory={setWorkHistory}
          />

          {/* ── Section 9: Onboarding (HR / Senior-HR only) ────────────────── */}
          <OnboardingSection
            personalEmail={personalEmail} refreshOnboarding={refreshOnboarding}
            sectionRef={sectionRefs.onboarding}
            isCreate={isCreate} isHR={isHR} isSrHR={isSrHR}
            onboardingRecord={onboardingRecord}
            initiateOnboarding={initiateOnboarding} initiatingOnboarding={initiatingOnboarding}
            isChecklistItemDone={isChecklistItemDone} docs={docs} scrollToSection={scrollToSection}
            recheckDoc={recheckDoc} recheckingDocId={recheckingDocId}
            togglingItemId={togglingItemId} toggleChecklistItem={toggleChecklistItem}
            navigate={navigate} employeeRouteId={id}
            docRefreshKeys={docRefreshKeys} bumpDocRefresh={bumpDocRefresh} setVerifyTarget={setVerifyTarget}
            activationBlockers={getOnboardingGateIssues()}
            locking={locking} unlockCompensation={unlockCompensation}
            compStatus={compHistory.some(h => h.status === "PENDING_VERIFICATION") ? "PENDING"
              : compHistory.some(h => h.status === "REJECTED") ? "REJECTED" : null}
            signedHandbook={signedHandbook} requestHandbookSignature={requestHandbookSignature} hrAcking={hrAcking}
            signLink={signLink} copySignLink={copySignLink} linkCopied={linkCopied} openSignPopup={openSignPopup}
            adStatus={adStatus} adLoading={adLoading} setAdConfirmOpen={setAdConfirmOpen} adConfirmOpen={adConfirmOpen}
            createAdAccount={createAdAccount} adCreating={adCreating}
            adExistsPrompt={adExistsPrompt} setAdExistsPrompt={setAdExistsPrompt} adResult={adResult}
            payrollChecking={payrollChecking} activating={activating} confirmReadyForPayroll={confirmReadyForPayroll}
            payrollResult={payrollResult}
          />

          {/* ── Section 10: Change History (last) ─────────────────────────── */}
          {!isCreate && (
            <ChangeHistorySection
              sectionRef={sectionRefs.history}
              auditLog={auditLog}
            />
          )}
          </SectionReadOnlyProvider>

        </div>
        </SectionPrivacyProvider>
        </SectionCollapseProvider>
      </EmployeeFormShell>

      {verifyTarget && (
        <DocumentLightbox
          target={verifyTarget}
          onClose={() => setVerifyTarget(null)}
          onDone={() => {
            bumpDocRefresh(verifyTarget.docType);
            setVerifyTarget(null);
          }}
        />
      )}
    </div>
  );
}
